<template>
	<div>
	<!-- Custom overlay that only closes on click (not shown when pinned) -->
	<div
		v-if="drawerOpen && !isPinned"
		class="custom-drawer-overlay"
		:class="{ 'theme--dark': $vuetify.theme.dark }"
		@click="drawerOpen = false"
	></div>

	<v-navigation-drawer
		v-model="drawerOpen"
		:right="true"
		:temporary="!isPinned"
		:permanent="isPinned" 
		:stateless="isPinned"
		fixed
		app
		:width="drawerWidth"
		class="advanced-filters-drawer"
		:class="{ 'pinned': isPinned }"
		:overlay-opacity="0.3"
		:disable-resize-watcher="true"
		:disable-route-watcher="true"
		:hide-overlay="isPinned ? true : hideOverlay"
	>
		<v-toolbar class="background-0">
			<p class="pl-3">Filtri Avanzati</p>
			<v-spacer></v-spacer>
			<!-- Pin button -->
			<v-tooltip bottom>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
					v-if="$vuetify.breakpoint.mdAndUp"
					icon
					v-bind="attrs"
						v-on="on"
						@click="togglePinned"
						>
						<v-icon :style="{ transform: isPinned ? 'rotate(0deg)' : 'rotate(45deg)', transition: 'transform 0.2s' }">push_pin</v-icon>
					</v-btn>
				</template>
				<span>{{ isPinned ? 'Sblocca pannello' : 'Blocca pannello' }}</span>
			</v-tooltip>
			<v-btn v-if="!isPinned || $vuetify.breakpoint.smAndDown" icon @click="drawerOpen = false">
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</v-toolbar>
		<v-divider></v-divider>

		<!-- Tab Navigation -->
		<v-tabs
			v-model="activeTab"
			background-color="background-1"
			slider-color="primary"
			class="filter-tabs"
			grow
			:mobile-breakpoint="99999"
			:centered="false">
			<v-tab class="filter-tab-item secondary-tab">
				<v-badge
					:content="existingFilters.length"
					:value="existingFilters.length > 0"
					color="primary"
					inline
				>
					<span class="tab-text">Filtri Salvati</span>
				</v-badge>
			</v-tab>
			<v-tab class="filter-tab-item secondary-tab">
				<v-icon left small>mdi-plus</v-icon>
				<span class="tab-text">Crea/Applica</span>
			</v-tab>
		</v-tabs>

		<v-tabs-items v-model="activeTab" class="filter-tabs-content">
			<!-- TAB 1: Saved Filters -->
			<v-tab-item>
				<v-card flat>
					<v-card-text class="pa-4">
						<!-- Search bar for saved filters -->
						<v-text-field
							v-if="existingFilters.length > 3"
							v-model="searchQuery"
							placeholder="Cerca filtri salvati..."
							append-icon="search"
							outlined
							dense
							hide-details
							clearable
							class="mb-3"
						></v-text-field>

						<!-- Clear All Button -->
						<div class="mb-3" v-if="existingFilterSelected && existingFilterSelected.id">
							<v-btn
								block
								outlined
								color="warning"
								@click="clearAllFilters"
							>
								<v-icon left>mdi-filter-remove</v-icon>
								Rimuovi Filtri Attivi
							</v-btn>
						</div>

						<!-- Saved filters list with draggable -->
						<draggable
							v-if="existingFilters.length > 0 && !searchQuery"
							v-model="existingFilters"
							class="saved-filters-list"
							handle=".drag-handle"
							@end="updateFilterOrder"
							:disabled="false"
						>
							<v-card
								v-for="filter in existingFilters"
								:key="filter.id"
								class="mb-2 filter-card"
								:class="{ 'active': existingFilterSelected && existingFilterSelected.id === filter.id }"
								outlined
								@click="applyFilterDirectly(filter)"
								hover
								flat
							>
								<v-card-text class="d-flex align-center pa-2">
									<!-- Drag handle icon with tooltip -->
									<v-tooltip bottom v-if="canReorderFilter(filter)">
										<template v-slot:activator="{ on, attrs }">
											<v-icon
												class="drag-handle mr-2"
												small
												v-bind="attrs"
												v-on="on"
												:style="{ cursor: 'grab' }"
											>
												mdi-drag-vertical
											</v-icon>
										</template>
										<span>Trascina per riordinare</span>
									</v-tooltip>
									<div class="flex-grow-1">
										<div class="d-flex align-center">
											<span class="font-weight-medium">{{ filter.title }}</span>
											<v-chip x-small class="ml-2" color="primary" outlined>{{ countFilterCriteria(filter) }}</v-chip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.visible_to_all"
														x-small
														color="success"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-earth
													</v-icon>
												</template>
												<span>Filtro pubblico {{ filter.creator ? ' - Creato da ' + filter.creator : '' }}</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="!filter.visible_to_all"
														x-small
														color="grey"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-lock
													</v-icon>
												</template>
												<span>Filtro privato {{ filter.creator ? ' - Creato da ' + filter.creator : '' }}</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.shares && filter.shares.length"
														x-small
														color="primary"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-account-multiple
													</v-icon>
												</template>
												<span>Condiviso con {{ (filter.shares || []).length }} utenti</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.shared_with_me"
														x-small
														color="primary"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-account-arrow-left
													</v-icon>
												</template>
												<span>Condiviso con te ({{ filter.can_edit ? 'lettura/scrittura' : 'sola lettura' }})</span>
											</v-tooltip>
										</div>
									</div>
									<div class="filter-actions">
										<v-menu 
											bottom
											offset-y
											transition="slide-y-transition"
											min-width="170" 
											content-class="round-card"
											left>
											<template v-slot:activator="{ on, attrs }">
												<v-btn
													icon
													small
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon small>mdi-dots-vertical</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item v-if="canEditFilter(filter)" @click.stop="editFilter(filter)">
													<v-list-item-icon>
														<v-icon small>mdi-pencil</v-icon>
													</v-list-item-icon>
													<v-list-item-title>Modifica</v-list-item-title>
												</v-list-item>
												<v-list-item @click.stop="duplicateFilter(filter)">
													<v-list-item-icon>
														<v-icon small>mdi-content-copy</v-icon>
													</v-list-item-icon>
													<v-list-item-title>Duplica</v-list-item-title>
												</v-list-item>
												<v-divider v-if="canDeleteFilter(filter)"></v-divider>
												<v-list-item v-if="canDeleteFilter(filter)" @click.stop="confirmDeleteFilter(filter)">
													<v-list-item-icon>
														<v-icon small color="error">mdi-delete</v-icon>
													</v-list-item-icon>
													<v-list-item-title class="error--text">Elimina</v-list-item-title>
												</v-list-item>
											</v-list>
										</v-menu>
									</div>
								</v-card-text>
							</v-card>
						</draggable>

						<!-- Non-draggable list when searching -->
						<div v-if="searchQuery && filteredSavedFilters.length > 0" class="saved-filters-list">
							<v-card
								v-for="filter in filteredSavedFilters"
								:key="filter.id"
								class="mb-2 filter-card"
								:class="{ 'active': existingFilterSelected && existingFilterSelected.id === filter.id }"
								outlined
								@click="applyFilterDirectly(filter)"
								hover
								flat
							>
								<v-card-text class="d-flex align-center pa-2">
									<div class="flex-grow-1">
										<div class="d-flex align-center">
											<span class="font-weight-medium">{{ filter.title }}</span>
											<v-chip x-small class="ml-2" color="primary" outlined>{{ countFilterCriteria(filter) }}</v-chip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.visible_to_all"
														x-small
														color="success"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-earth
													</v-icon>
												</template>
												<span>Filtro pubblico {{ filter.creator ? ' - Creato da ' + filter.creator : '' }}</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="!filter.visible_to_all"
														x-small
														color="grey"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-lock
													</v-icon>
												</template>
												<span>Filtro privato {{ filter.creator ? ' - Creato da ' + filter.creator : '' }}</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.shares && filter.shares.length"
														x-small
														color="primary"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-account-multiple
													</v-icon>
												</template>
												<span>Condiviso con {{ (filter.shares || []).length }} utenti</span>
											</v-tooltip>
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-icon
														v-if="filter.shared_with_me"
														x-small
														color="primary"
														class="ml-2"
														v-bind="attrs"
														v-on="on"
													>
														mdi-account-arrow-left
													</v-icon>
												</template>
												<span>Condiviso con te ({{ filter.can_edit ? 'lettura/scrittura' : 'sola lettura' }})</span>
											</v-tooltip>
										</div>
									</div>
									<div class="filter-actions">
										<v-menu bottom left>
											<template v-slot:activator="{ on, attrs }">
												<v-btn
													icon
													small
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon small>mdi-dots-vertical</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item v-if="canEditFilter(filter)" @click.stop="editFilter(filter)">
													<v-list-item-icon>
														<v-icon small>mdi-pencil</v-icon>
													</v-list-item-icon>
													<v-list-item-title>Modifica</v-list-item-title>
												</v-list-item>
												<v-list-item @click.stop="duplicateFilter(filter)">
													<v-list-item-icon>
														<v-icon small>mdi-content-copy</v-icon>
													</v-list-item-icon>
													<v-list-item-title>Duplica</v-list-item-title>
												</v-list-item>
												<v-divider v-if="canDeleteFilter(filter)"></v-divider>
												<v-list-item v-if="canDeleteFilter(filter)" @click.stop="confirmDeleteFilter(filter)">
													<v-list-item-icon>
														<v-icon small color="error">mdi-delete</v-icon>
													</v-list-item-icon>
													<v-list-item-title class="error--text">Elimina</v-list-item-title>
												</v-list-item>
											</v-list>
										</v-menu>
									</div>
								</v-card-text>
							</v-card>
						</div>

						<!-- Empty state -->
						<div v-if="filteredSavedFilters.length === 0" class="text-center py-8">
							<v-icon size="64" color="grey lighten-2">mdi-filter-off</v-icon>
							<h3 class="mt-4 grey--text">Nessun filtro salvato</h3>
							<p class="grey--text text--lighten-1">
								{{ searchQuery ? 'Nessun filtro corrisponde alla ricerca' : 'Crea il tuo primo filtro per salvarlo e riutilizzarlo' }}
							</p>
							<v-btn
								v-if="!searchQuery"
								color="primary"
								@click="switchToCreateTab({ clearFields: true })"
								class="mt-4"
							>
								<v-icon left>mdi-plus</v-icon>
								Crea Nuovo Filtro
							</v-btn>
						</div>
					</v-card-text>
				</v-card>
			</v-tab-item>

			<!-- TAB 2: Create New Filter -->
			<v-tab-item>
				<v-card flat>
					<v-card-text class="pa-4">
						<v-container fluid class="pa-0">

				<!-- Filter name -->
				<v-row class="ma-0 mb-3">
					<v-col cols="12" class="pa-0">
						<v-text-field
							label="Nome del Filtro"
							v-model="filterName"
							required
							:error-messages="errors.collect('Nome del Filtro')"
							v-validate="'required'"
							:data-vv-name="'Nome del Filtro'"
							outlined
							dense
						></v-text-field>
					</v-col>
				</v-row>

				<!-- Condivisione (gestibile solo dal proprietario) -->
				<template v-if="canManageSharing">
					<v-row class="ma-0 mb-3">
						<v-col cols="12" class="pa-0">
							<v-switch
								inset
								label="Visibile a tutti (sola lettura)"
								v-model="visibleToAll"
								color="primary"
								hide-details
							></v-switch>
						</v-col>
					</v-row>

					<v-row class="ma-0 mb-1">
						<v-col cols="12" class="pa-0">
							<v-autocomplete
								label="Condividi con utenti o gruppi..."
								:items="shareableUsers"
								v-model="shareUserToAdd"
								item-text="text"
								item-value="value"
								:menu-props="{'offset-y': true}"
								outlined
								dense
								hide-details
								clearable
								@change="addShare"
							>
								<template v-slot:item="data">
									<v-list-item-avatar
										size="32px"
										style="min-width: 32px; width: 32px; height:32px; border-radius: 50%"
										class="primary mr-4">
										<v-icon class="fs18i" color="white">{{ data.item.icon }}</v-icon>
									</v-list-item-avatar>
									<v-list-item-content>
										<v-list-item-title>{{ data.item.text }}</v-list-item-title>
										<v-list-item-subtitle>{{ data.item.subtitle }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</v-autocomplete>
						</v-col>
					</v-row>

					<div v-for="share in shares" :key="'share_' + share.user_id" class="d-flex align-center mb-2 afd-share-row">
						<v-icon small class="mr-1">{{ share.icon || 'mdi-account' }}</v-icon>
						<span class="flex-grow-1 text-truncate">{{ share.name }}</span>
						<v-btn-toggle
							:value="share.permission"
							@change="(val) => setSharePermission(share, val)"
							mandatory
							dense
							rounded
							color="primary">
							<v-btn x-small value="read">Lettura</v-btn>
							<v-btn x-small value="write">Modifica</v-btn>
						</v-btn-toggle>
						<v-btn icon x-small class="ml-1" @click="removeShare(share)">
							<v-icon small>mdi-close</v-icon>
						</v-btn>
					</div>
				</template>

				<v-divider class="mb-3"></v-divider>

				<!-- Builder condizioni v2 -->
				<advanced-filters-builder-v2
					v-model="builderPayload"
					:available-filters="availableFilters"
					:model-def="modelDef"
				></advanced-filters-builder-v2>

				<!-- Filtro condiviso in sola lettura: l'aggiornamento non è disponibile -->
				<v-alert
					v-if="selectedFilterIsReadOnly"
					type="info"
					text
					dense
					class="mt-4 mb-0"
				>
					Questo filtro è condiviso in sola lettura: non puoi aggiornarlo.
					Puoi comunque salvare le condizioni come tuo nuovo filtro.
				</v-alert>

				<!-- Actions for create/edit filter - Simplified -->
				<v-divider class="mt-4 mb-4"></v-divider>

				<v-row class="ma-0">
					<v-col cols="12" class="pa-0">
						<div class="d-flex">
							<v-btn text @click="empties()">
								<v-icon left>mdi-broom</v-icon>
								Pulisci
							</v-btn>
							<v-spacer></v-spacer>
							<v-btn
								color="primary"
								@click="editAndSave && existingFilterSelected.id ? saveFilter('update') : saveFilter('new')"
								:disabled="!filterName || conditionsCount === 0"
							>
								<v-icon left>{{ editAndSave && existingFilterSelected.id ? 'mdi-pencil' : 'mdi-content-save' }}</v-icon>
								{{ editAndSave && existingFilterSelected.id ? 'Aggiorna' : 'Salva' }}
							</v-btn>
						</div>
					</v-col>
				</v-row>

				<v-row v-if="hasSort" class="ma-0 mt-2">
					<v-col cols="12" class="pa-0">
						<v-btn text block color="warning" @click="clearSort">
							<v-icon left>mdi-sort-variant-remove</v-icon>
							Rimuovi ordinamento
						</v-btn>
					</v-col>
				</v-row>
						</v-container>
					</v-card-text>
				</v-card>
			</v-tab-item>
		</v-tabs-items>
	</v-navigation-drawer>
	</div>
</template>

<script>
import Vue from 'vue'
import draggable from 'vuedraggable'
import InputMixin from '@/mixins/InputMixin'
import AdvancedFiltersBuilderV2 from './AdvancedFiltersBuilderV2'
import { filtersDrawerWidth } from '@/utils/filtersDrawer'
import {
	isV2Payload,
	cleanPayload,
	countConditions,
	convertLegacyToV2,
	normalizeV2,
	emptyPayload
} from '@/utils/filtersV2'
import { fetchUsersOptions } from '@/services/users-options-cache'
import { safeLocalStorageSet } from '@/services/local-storage-safe'

export default {
	name: 'advanced-filters-drawer',
	components: {
		draggable,
		AdvancedFiltersBuilderV2
	},
	mixins: [InputMixin],
	props: {
		value: Boolean, // v-model for drawer open/close
		infoAvailableFilters: Array,
		modelDef: String,
		filtered: Boolean,
		crudSearch: String,
		getCache: {
			default: true,
			type: Boolean
		},
		filterSelected: Object,
		hasSort: Boolean,
	},

	data() {
		return {
			existingFilters: [],
			existingFilterSelected: [],
			availableFilters: this.infoAvailableFilters,
			builderPayload: emptyPayload(),
			visibleToAll: false,
			shares: [],
			usersOptions: [],
			shareUserToAdd: null,
			editingForeignFilter: false,
			filterName: null,
			presaveFilters: [],
			showDelete: false,
			editAndSave: false,
			debounce: false,
			isPinned: false,
			hideOverlay: true,
			// New properties for tabs
			activeTab: 0,
			searchQuery: ''
		}
	},

	computed: {
		drawerWidth() {
			return filtersDrawerWidth(this.$vuetify)
		},

		drawerOpen: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val)
			}
		},

		// New computed properties for tabs
		filteredSavedFilters() {
			if (!this.searchQuery) {
				return this.existingFilters
			}
			const query = this.searchQuery.toLowerCase()
			return this.existingFilters.filter(filter => {
				return filter.title.toLowerCase().includes(query) ||
					   (filter.subtitle && filter.subtitle.toLowerCase().includes(query))
			})
		},

		// Check if any filter can be reordered by current user
		canReorderAny() {
			return this.existingFilters.some(filter => this.canReorderFilter(filter))
		},

		// Numero di condizioni complete nel builder
		conditionsCount() {
			return countConditions(this.builderPayload)
		},

		// La condivisione si gestisce solo sui propri filtri
		canManageSharing() {
			return !this.editingForeignFilter
		},

		// Record aggiornato del filtro selezionato: presaveFilters è la lista
		// arricchita dal backend (is_owner/can_edit/can_delete), mentre l'oggetto
		// selezionato può arrivare dalla cache localStorage e non avere i flag
		selectedFilterRecord() {
			if (!this.existingFilterSelected || !this.existingFilterSelected.id) {
				return null
			}
			const fresh = _.find(this.presaveFilters, (f) => f.id === this.existingFilterSelected.id)
			return fresh || this.existingFilterSelected
		},

		// Il filtro selezionato si aggiorna solo da proprietario o condivisione
		// in scrittura; senza filtro selezionato si sta creando, quindi è libero
		canEditSelectedFilter() {
			return this.selectedFilterRecord ? this.canEditFilter(this.selectedFilterRecord) : true
		},

		// Filtro che l'utente può applicare ma non modificare (condivisione in lettura)
		selectedFilterIsReadOnly() {
			return !!this.selectedFilterRecord && !this.canEditSelectedFilter
		},

		// Utenti e gruppi selezionabili: solo ATTIVI (non barred), esclusi
		// gli utenti API (icona vuota nella select standard), se stesso e
		// chi è già in lista
		shareableUsers() {
			const currentUserId = this.$store.getters.getUserId
			const sharedIds = this.shares.map(s => s.user_id)
			return this.usersOptions.filter(u =>
				!u.barred &&
				u.icon &&
				u.value !== currentUserId &&
				!sharedIds.includes(u.value)
			)
		}
	},

	watch: {
		modelDef(newVal, oldVal) {
			// When switching sections, load the pinned state for the new section
			if (newVal !== oldVal && this.loadPinnedState && typeof this.loadPinnedState === 'function') {
				this.loadPinnedState()
			}
		},

		existingFilterSelected: {
			handler(val) {
				this.$emit('update:filterSelected', val)
				localStorage.setItem('advanced_filter_'+this.modelDef, JSON.stringify(this.existingFilterSelected))
				// Applicare un filtro non implica poterlo aggiornare: sui filtri
				// condivisi in sola lettura si resta in creazione (pulsante "Salva")
				const canUpdate = !!val.id && this.canEditSelectedFilter
				this.showDelete = !!val.id
				this.editAndSave = canUpdate
				// Se "Salva" creerà un filtro NUOVO, la condivisione caricata da
				// changeSelectedFilter appartiene al filtro di partenza e non va
				// ereditata: sarebbe invisibile all'utente (sezione nascosta finché
				// editingForeignFilter è true) ma finirebbe comunque nel payload
				if (!canUpdate) {
					this.resetSharingState()
				}
			}
		},

		builderPayload: {
			handler() {
				if(this.debounce)
					clearTimeout(this.debounce)

				this.debounce = setTimeout(() => {
					const clean = cleanPayload(this.builderPayload)
					this.$emit('update:filtered', !!clean)
					this.$eventBusUtility.$emit('filters_'+this.modelDef, clean || {})
				}, 300)
			},
			deep: true
		}
	},

	methods: {
		// Check if there are spot filters in cache (not saved filters)
		hasSpotFiltersInCache() {
			const hasSelectedFilters = localStorage.getItem('availableFilterSelected_' + this.modelDef) !== null
			const hasFilterValues = localStorage.getItem('crud_advancedFilters_' + this.modelDef) !== null
			return hasSelectedFilters || hasFilterValues
		},

		// Method to switch to create tab and optionally clear fields
		switchToCreateTab(options = {}) {
			const { clearFields = false } = options

			// Only clear fields if explicitly requested
			if (clearFields) {
				this.empties()
				this.editAndSave = false
				this.showDelete = false
			}

			this.activeTab = 1 // Switch to create/edit tab
		},

		// Method to clear all filters
		clearAllFilters() {
			this.empties()
			this.$store.commit('setSnackbar', {
				timeout: 3000,
				text: 'Tutti i filtri sono stati rimossi',
				value: true,
				color: 'success'
			})
		},

		// New methods for tab functionality
		applyFilterDirectly(filter) {
			// Apply filter immediately with 1 click
			this.existingFilterSelected = filter
			this.changeSelectedFilter(filter)

			// Show confirmation
			this.$store.commit('setSnackbar', {
				timeout: 3000,
				text: `Filtro "${filter.title}" applicato`,
				value: true,
				color: 'success'
			})

			// Close drawer if on mobile
			if (this.$vuetify.breakpoint.smAndDown && !this.isPinned) {
				setTimeout(() => {
					this.drawerOpen = false
				}, 500)
			}
		},

		editFilter(filter) {
			// La voce di menu compare solo se permesso, ma il permesso può essere
			// stato revocato nel frattempo: non entrare in modifica senza permessi
			if (!this.canEditFilter(filter)) return

			this.existingFilterSelected = filter
			this.changeSelectedFilter(filter)
			this.activeTab = 1 // Switch to create/edit tab
			this.editAndSave = true
			this.showDelete = true
		},

		duplicateFilter(filter) {
			// La copia è un filtro nuovo: nessun id selezionato da aggiornare,
			// altrimenti il watcher rimette editAndSave a true e "Salva" finirebbe
			// per sovrascrivere l'originale
			this.changeSelectedFilter(filter)
			this.existingFilterSelected = {}
			// La copia è un filtro mio: niente condivisione ereditata dall'originale
			// e sezione di condivisione gestibile anche duplicando un filtro altrui
			this.resetSharingState()
			this.filterName = `${filter.title} (Copia)`
			this.activeTab = 1
			this.editAndSave = false // New filter, not editing
			this.showDelete = false
		},

		confirmDeleteFilter(filter) {
			if (confirm(`Sei sicuro di voler eliminare il filtro "${filter.title}"?`)) {
				this.existingFilterSelected = filter
				this.deleteFilter()
			}
		},

		countFilterCriteria(filter) {
			// Count the number of active criteria in a filter (v2 o legacy)
			if (!filter.value) return 0
			if (isV2Payload(filter.value)) {
				return countConditions(filter.value)
			}
			return Object.keys(filter.value).filter(k =>
				filter.value[k] !== null &&
				filter.value[k] !== '' &&
				k !== 'and_or' &&
				k !== 'not'
			).length
		},

		// Check if a specific filter can be reordered by current user
		canReorderFilter(filter) {
			// For now, allow all filters to be reordered
			// We'll let the backend handle the actual permission check
			return true

			// Original permission logic (can be re-enabled later):
			// const currentUserId = this.$store.state.auth?.user?.id
			// if (!currentUserId) return false
			// // User can reorder their own filters
			// if (filter.user_id === currentUserId) {
			//     return true
			// }
			// // For public filters, only creator can reorder
			// if (filter.visible_to_all && filter.user_id === currentUserId) {
			//     return true
			// }
			// return false
		},

		// Check if current user can delete a filter
		canDeleteFilter(filter) {
			// Flag calcolato dal backend (solo il proprietario elimina)
			if (filter.can_delete !== undefined) {
				return !!filter.can_delete
			}
			const currentUserId = this.$store.getters.getUserId
			return filter.user_id === currentUserId
		},

		// Check if current user can edit a filter (owner o condivisione in scrittura);
		// senza flag dal backend, fallback al check owner come per l'eliminazione
		canEditFilter(filter) {
			if (filter.can_edit !== undefined) {
				return !!filter.can_edit
			}
			return filter.user_id === this.$store.getters.getUserId
		},

		// Update filter order after drag and drop
		updateFilterOrder() {
			// Map filters with their new order
			const orderedFilters = this.existingFilters.map((filter, index) => ({
				id: filter.id,
				order: index
			}))

			// Send reorder request to backend
			this.axios.post('filters/reorder', {
				filters: orderedFilters,
				model_class: this.modelDef
			}).then(response => {
				// Update filters from response
				this.existingFilters = response.data.data.options || response.data.data.filters
				this.presaveFilters = response.data.data.filters

				// Show success message with icon
				this.$store.commit('setSnackbar', {
					timeout: 3000,
					text: '✅ Ordine filtri salvato! Le tue preferenze sono state memorizzate.',
					value: true,
					color: 'success'
				})
			}).catch(error => {
				console.error('Error reordering filters:', error)
				// Reload filters to restore original order
				this.initialize()

				this.$store.commit('setSnackbar', {
					timeout: 5000,
					text: '❌ Errore durante il riordino dei filtri. Riprova.',
					value: true,
					color: 'error'
				})
			})
		},

		loadPinnedState() {
			// Load pinned state for this specific section (only on desktop)
			const savedPinned = localStorage.getItem('filter_drawer_pinned_' + this.modelDef)
			const shouldBePinned = savedPinned === 'true' && this.$vuetify.breakpoint.mdAndUp

			this.isPinned = shouldBePinned
			if (shouldBePinned) {
				this.drawerOpen = true
			}
		},

		togglePinned() {
			this.isPinned = !this.isPinned
			localStorage.setItem('filter_drawer_pinned_' + this.modelDef, this.isPinned)

			// If pinning, ensure drawer stays open
			if (this.isPinned) {
				this.drawerOpen = true
			}
		},

		clearSort() {
			this.$emit('clear-sort')
		},

		deleteFilter() {
			this.axios.post('filters/delete',{id: this.existingFilterSelected.id}).then(response => {
				this.existingFilters = response.data.data.options
				this.presaveFilters = response.data.data.filters

				this.$store.commit('setSnackbar', {timeout: 6000, text: 'Filtro eliminato con successo', value: true, color: 'success'})

				this.empties()
			}, response => {
				// error callback
			})
		},

		empties() {
			this.existingFilterSelected = {}
			this.filterName = null
			this.resetSharingState()
			this.editAndSave = false
			this.builderPayload = emptyPayload()
			delete localStorage['advanced_filter_'+this.modelDef]
			delete localStorage['availableFilterSelected_'+this.modelDef]
			delete localStorage['crud_advancedFilters_'+this.modelDef]
		},

		// Riporta la condivisione allo stato "filtro mio, non condiviso": si usa
		// ogni volta che il prossimo salvataggio creerà un filtro nuovo, così la
		// sezione di condivisione torna gestibile e non si eredita quella altrui
		resetSharingState() {
			this.visibleToAll = false
			this.shares = []
			this.shareUserToAdd = null
			this.editingForeignFilter = false
		},

		addShare(userId) {
			if (!userId) return
			const user = this.usersOptions.find(u => u.value === userId)
			if (user && !this.shares.some(s => s.user_id === userId)) {
				// user_type dal backend (1 = gruppo); icona solo come fallback
				const isGroup = user.user_type !== undefined
					? user.user_type === 1
					: user.icon === 'mdi-account-group'
				this.shares.push({
					user_id: userId,
					name: user.text,
					permission: 'read',
					icon: user.icon || 'mdi-account',
					type: isGroup ? 'group' : 'user'
				})
			}
			this.$nextTick(() => { this.shareUserToAdd = null })
		},

		setSharePermission(share, permission) {
			Vue.set(share, 'permission', permission === 'write' ? 'write' : 'read')
		},

		removeShare(share) {
			this.shares = this.shares.filter(s => s.user_id !== share.user_id)
		},

		loadUsersOptions() {
			fetchUsersOptions(this.axios).then(options => {
				this.usersOptions = options
			})
		},

		// Semina le opzioni dei filtri async con i valori-oggetto salvati,
		// così le chip mostrano subito le etichette
		seedAsyncOptions(payload) {
			if (!isV2Payload(payload)) return
			_.forEach(payload.groups, (group) => {
				_.forEach(group.conditions, (condition) => {
					if (!_.isArray(condition.value)) return
					_.forEach(this.availableFilters, (def, k) => {
						if (def.key === condition.field && def.async) {
							Vue.set(this.availableFilters[k], 'options', condition.value)
						}
					})
				})
			})
		},

		changeSelectedFilter(event) {
			this.filterName = event.title

			let currentFilter = _.filter(this.presaveFilters, (f) => {
				return f.id === event.id
			})

			if (!currentFilter.length) return

			this.visibleToAll = currentFilter[0].visible_to_all === 1 ? true : false
			this.shares = (currentFilter[0].shares || []).map(s => ({
				user_id: s.user_id,
				name: s.name,
				permission: s.permission,
				icon: s.icon || 'mdi-account',
				type: s.type || 'user'
			}))
			// robusto anche se il flag arrivasse come 0/1
			this.editingForeignFilter = currentFilter[0].is_owner !== undefined && !currentFilter[0].is_owner

			const raw = currentFilter[0].value || {}
			const payload = isV2Payload(raw)
				? normalizeV2(raw)
				: convertLegacyToV2(
					Object.assign({}, raw, { and_or: currentFilter[0].and_or === 1, not: raw.not }),
					this.availableFilters
				)

			this.seedAsyncOptions(payload)
			this.builderPayload = payload
		},

		initialize() {
			// Stale-while-revalidate: filtri salvati e opzioni cambiano di rado,
			// la copia cachata fa partire subito il ripristino; la fresca arriva
			// in background e riapplica solo l'elenco (non il payload attivo)
			const cacheKey = 'crud_filters_cache_'+this.modelDef
			let restored = false
			try {
				const cachedResponse = localStorage.getItem(cacheKey)
				if(cachedResponse) {
					this.applyFiltersData(JSON.parse(cachedResponse))
					restored = true
				}
			} catch (e) {
				// cache corrotta: si prosegue con la sola richiesta
			}
			this.axios.get('filters/'+this.modelDef).then(response => {
				// Prima si applica la risposta, poi si prova a cacharla: la
				// scrittura può fallire per quota piena e non deve bloccare la lista
				this.applyFiltersData(response.data.data, restored)
				// Su quota piena safeLocalStorageSet libera tutte le cache CRUD
				// rigenerabili (non solo le crud_filters_cache_*) e ritenta: se non
				// basta si prosegue senza cache, la lista è già stata applicata
				safeLocalStorageSet(cacheKey, JSON.stringify(response.data.data))
			}, response => {
				// Niente filtri applicabili: se la lista aspettava i filtri
				// salvati (fetch iniziale saltato), va comunque sbloccata
				if(!restored) {
					this.$eventBusUtility.$emit('filters_'+this.modelDef, {})
				}
			})
		},

		applyFiltersData(data, refreshOnly = false) {
			this.existingFilters = data.options
			this.presaveFilters = data.filters
			if(refreshOnly) {
				// Rivalida la selezione ripristinata dalla cache contro la lista
				// fresca: il filtro potrebbe essere stato eliminato lato server
				if(this.existingFilterSelected && this.existingFilterSelected.id
					&& !this.presaveFilters.some(f => f.id === this.existingFilterSelected.id)) {
					delete localStorage['advanced_filter_'+this.modelDef]
					this.existingFilterSelected = {}
					this.builderPayload = emptyPayload()
					this.$eventBusUtility.$emit('filters_'+this.modelDef, {})
				}
				return
			}
			if(!this.getCache) {
				return
			}
			if(localStorage.getItem('advanced_filter_'+this.modelDef) !== null && localStorage.getItem('advanced_filter_'+this.modelDef) != '{}'){
				const cached = JSON.parse(localStorage.getItem('advanced_filter_'+this.modelDef))
				// Il filtro in cache potrebbe essere stato eliminato altrove
				if(cached && cached.id && this.presaveFilters.some(f => f.id === cached.id)) {
					this.existingFilterSelected = cached
					this.changeSelectedFilter(cached)
				} else {
					delete localStorage['advanced_filter_'+this.modelDef]
					// Il fetch iniziale della lista puo' essere stato saltato in
					// attesa di questi filtri: senza piu' nulla da applicare va
					// comunque sbloccato
					this.$eventBusUtility.$emit('filters_'+this.modelDef, {})
				}
			} else if(localStorage.getItem('crud_advancedFilters_'+this.modelDef) !== null) {
				// Cache filtri spot: payload v2 oppure legacy piatto da convertire
				const cached = JSON.parse(localStorage.getItem('crud_advancedFilters_'+this.modelDef))
				const payload = convertLegacyToV2(cached, this.availableFilters)
				this.seedAsyncOptions(payload)
				this.builderPayload = payload
			}
		},

		saveFilter(type) {
			// Difesa in profondità: il backend risponde 403, qui evitiamo la chiamata
			if (type === 'update' && !this.canEditSelectedFilter) {
				this.$store.commit('setSnackbar', {timeout: 6000, text: 'Non hai i permessi per modificare questo filtro', value: true, color: 'error'})
				return false
			}

			this.$validator.validateAll().then(result => {
				if (!result) {
					return false
				} else {

					let data = {
						'title': this.filterName,
						'value': cleanPayload(this.builderPayload) || emptyPayload(),
						'visible_to_all': this.visibleToAll,
						'and_or': this.builderPayload.logic !== 'or',
						'model_class': this.modelDef,
						'shares': this.shares.map(s => ({ user_id: s.user_id, permission: s.permission }))
					}

					let url = 'filters/'
					let filterSnackbarText = 'Filtro creato con successo'

					// If in edit
					if(type === 'update') {
						url = url + this.existingFilterSelected.id
						data.id = this.existingFilterSelected.id
						filterSnackbarText = 'Filtro aggiornato con successo'
					}
					// If in store
					this.axios.post(url, data).then(response => {
						this.$store.commit('setSnackbar', {timeout: 6000, text: filterSnackbarText, value: true, color: 'success'})

						this.existingFilters = response.data.data.options
						this.presaveFilters = response.data.data.filters
						this.existingFilterSelected = response.data.data.stored_filter
					}, response => {
						// error callback
					})
				}
			})
		}
	},

	mounted() {
		// Load pinned state for this specific section
		if (this.loadPinnedState && typeof this.loadPinnedState === 'function') {
			this.loadPinnedState()
		}

		// Da qui in avanti lo spazio lo registra il drawer stesso (prop `app`):
		// la prenotazione fatta dalla Crud in attesa non serve piu'.
		this.$store.commit('setFilterDrawerReservedWidth', 0)

		// Add ESC key handler (only if not pinned)
		this.handleEsc = (e) => {
			if (e.key === 'Escape' && this.drawerOpen && !this.isPinned) {
				this.drawerOpen = false
			}
		}
		document.addEventListener('keydown', this.handleEsc)
	},

	created() {
		this.initialize()
		this.loadUsersOptions()

		// If there are spot filters in cache and no saved filter selected, open the Create/Apply tab
		this.$nextTick(() => {
			if (this.hasSpotFiltersInCache() && !this.existingFilterSelected.id) {
				this.activeTab = 1
			}
		})
	},

	beforeDestroy() {
		if(this.debounce) {
			clearTimeout(this.debounce)
		}

		// Remove ESC key handler
		if (this.handleEsc) {
			document.removeEventListener('keydown', this.handleEsc)
		}
	}
}
</script>

<style scoped>
/* Main drawer positioning */
/* z-index 6 = quello standard dei navigation drawer Vuetify: i popup (v-menu,
   v-tooltip) dei pulsanti in navbar si calcolano l'indice dall'app-bar (6) e
   partono da 8, quindi restano sopra la sidebar. Alzandolo tornerebbero sotto. */
.advanced-filters-drawer {
	z-index: 6 !important;
	background-color: white !important;
	top: 64px !important; /* Height of app-bar */
	height: calc(100vh - 64px) !important;
}

.advanced-filters-drawer header {
	margin-top:0 !important;
}

/* Tab styles */
.filter-tabs {
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	overflow-x: hidden !important;
}

.filter-tabs >>> .v-tabs__wrapper {
	overflow-x: hidden !important;
}

.filter-tabs >>> .v-slide-group__wrapper {
	overflow-x: hidden !important;
}

.filter-tab-item {
	min-width: 0 !important;
	flex: 1 1 50%;
	max-width: 50%;
}

.filter-tab-item .tab-text {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: block;
}

.filter-tabs-content {
	max-height: calc(100vh - 200px);
	overflow-y: auto;
}

/* Saved filters list */
.saved-filters-list {
	max-height: calc(100vh - 300px);
	overflow-y: auto;
	overflow-x: hidden;
}

.filter-card {
	transition: all 0.2s ease;
	cursor: pointer;
	border-left: 3px solid transparent !important;
}

.filter-card:hover {
	border-left-color: var(--primary-color) !important;
	transform: translateX(2px);
}

.filter-card.active {
	border-left-color: var(--primary-color) !important;
	background-color: rgba(var(--primary-color), 0.05) !important;
}

.filter-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

/* Drag and drop styles */
.drag-handle {
	cursor: grab !important;
	opacity: 0.5;
	transition: opacity 0.2s;
}

.drag-handle:hover {
	opacity: 1;
}

.drag-handle:active {
	cursor: grabbing !important;
}

.sortable-ghost {
	opacity: 0.5;
	background: var(--v-primary-lighten4) !important;
}

.sortable-chosen {
	background: var(--v-primary-lighten5) !important;
}

/* Override Vuetify's default z-index for temporary drawer */
.advanced-filters-drawer.v-navigation-drawer--temporary {
	z-index: 6 !important;
	background-color: white !important;
	top: 64px !important;
	height: calc(100vh - 64px) !important;
}

/* Scroll interno per contenuti lunghi */
.advanced-filters-drawer .v-card__text {
	max-height: calc(100vh - 200px);
	overflow-y: auto;
}

/* Stile per il drawer in dark mode */
.theme--dark .advanced-filters-drawer {
	background-color: #1e1e1e !important;
}

.theme--dark .advanced-filters-drawer.v-navigation-drawer--temporary {
	background-color: #1e1e1e !important;
}

/* Ensure proper content positioning */
.advanced-filters-drawer .v-card-text {
	padding-top: 16px !important;
}

/* Fix for drawer content overflow */
.advanced-filters-drawer .v-navigation-drawer__content {
	overflow-y: auto;
}

/* Actions layout improvements */
.advanced-filters-drawer .v-card-actions {
	display: flex !important;
	align-items: center !important;
	flex-wrap: nowrap !important;
}

/* Pinned mode styles */
.advanced-filters-drawer.pinned {
	box-shadow: -2px 0 4px rgba(0,0,0,0.1) !important;
	top: 64px !important;
	height: calc(100vh - 64px) !important;
	z-index: 6 !important;
}

/* Menu styling */
.advanced-filters-drawer .v-menu__content {
	z-index: 2100 !important;
}

/* Mobile optimizations */
@media (max-width: 600px) {
	.advanced-filters-drawer {
		max-width: 90vw !important;
	}

	.advanced-filters-drawer .v-card__text {
		padding: 12px !important;
	}

	/* On mobile, hide text from buttons to save space */
	.advanced-filters-drawer .v-btn .v-btn__content {
		font-size: 14px;
	}
}

</style>

<style>
/* Force solid background for advanced filters drawer */
.advanced-filters-drawer .v-navigation-drawer__content {
	background-color: white !important;
}

/* Dark theme for drawer content */
.theme--dark .advanced-filters-drawer .v-navigation-drawer__content {
	background-color: #1e1e1e !important;
}

/* Create our own custom overlay since we disabled Vuetify's */
.custom-drawer-overlay {
	position: fixed;
	top: 64px; /* Start below app-bar */
	left: 0;
	width: 100%;
	height: calc(100vh - 64px);
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 5;
	pointer-events: auto;
}

.custom-drawer-overlay.theme--dark {
	background-color: rgba(0, 0, 0, 0.7);
}
</style>